// ===== BEGIN FILE: src/utils/fetchHouseInterpretations.ts =====
/**
 * Fetch comprehensive house interpretation data for AI chat context
 * 
 * Retrieves all proprietary interpretation data from:
 * - planet_in_house_predictions (overview + nakshatra notes)
 * - houses (planet notes, sign notes, actionables, cautions, remedies)
 * - house_lord_in_house_predictions (lordship combinations)
 */

import { supabase } from './supabaseClient';
import type { PlanetName } from './astroEngine';
import { OWN_SIGNS } from './astroEngine';

const PLANETS_7: Exclude<PlanetName, 'Rahu' | 'Ketu'>[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'];
const ALL_PLANETS: PlanetName[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];

// ===== SHARED CACHE FOR LORD-IN-HOUSE PREDICTIONS (PERMANENT - NEVER EXPIRES) =====
// Cache key bumped to v8 — moderated harsh classical text in house_lord_in_house_predictions
const CACHE_KEY_LIH = 'interp_lih_v8';
const CACHE_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000; // 1 year (permanent)

let lihMemoryCache: Map<number, any> | null = null; // key: destHouseId (bump CACHE_KEY_LIH to reset)
let lihFetchPromise: Promise<Map<number, any>> | null = null;

async function getCachedLordInHousePredictions(): Promise<Map<number, any>> {
  // Check memory cache
  if (lihMemoryCache) return lihMemoryCache;

  // Check localStorage
  try {
    const item = localStorage.getItem(CACHE_KEY_LIH);
    if (item) {
      const parsed = JSON.parse(item);
      if (Date.now() - parsed.timestamp < CACHE_EXPIRY_MS) {
        lihMemoryCache = new Map(parsed.data);
        return lihMemoryCache;
      }
      localStorage.removeItem(CACHE_KEY_LIH);
    }
  } catch {}

  // If fetch in progress, await it
  if (lihFetchPromise) return lihFetchPromise;

  // Fetch ALL rows in one query — one row per dest_house_id
  lihFetchPromise = (async () => {
    const { data, error } = await supabase
      .from('house_lord_in_house_predictions')
      .select('*');

    const map = new Map<number, any>();
    if (!error && data) {
      data.forEach((row: any) => {
        // Table has one row per dest_house_id — generic "any lord in this house"
        if (!map.has(row.dest_house_id)) {
          map.set(row.dest_house_id, row);
        }
      });
    }

    lihMemoryCache = map;
    try {
      localStorage.setItem(CACHE_KEY_LIH, JSON.stringify({ data: Array.from(map.entries()), timestamp: Date.now() }));
    } catch {}

    lihFetchPromise = null;
    return map;
  })();

  return lihFetchPromise;
}

export interface HouseInterpretation {
  house: number;
  overview?: string; // House description
  planets?: Array<{
    planet: string;
    interpretation?: string; // Main planet-in-house prediction
    nakshatraNote?: string; // Nakshatra-specific note
    nakshatraLordNote?: string; // Nakshatra lord note
    housePlanetNote?: string; // House-specific planet note
  }>;
  lordship?: Array<{
    lord: string;
    position: string; // e.g., "H5 lord in H10"
    interpretation?: string; // Lordship prediction
    overview?: string;
    strengths?: string;
    challenges?: string;
    remedies?: string;
  }>;
  signNote?: string; // Sign-in-house note
}

/**
 * Helper: Get sign from longitude
 */
function getSignFromLongitude(lon: number): number {
  const normalized = ((lon % 360) + 360) % 360;
  return Math.floor(normalized / 30) + 1;
}

/**
 * Helper: Get house number where sign sits (whole-sign system)
 */
function houseSign(ascSign1to12: number, house: number): number {
  return ((ascSign1to12 + house - 2 + 12) % 12) + 1;
}

/**
 * Main function: Fetch all house interpretations
 */
export async function fetchHouseInterpretations(
  ascendantSign: number,
  placements: Partial<Record<PlanetName, { house: number; offsetDeg: number }>>,
  longitudes: Partial<Record<PlanetName, number>>
): Promise<HouseInterpretation[]> {
  
  try {
    // 1. Fetch planet IDs
    const { data: planetMeta, error: planetErr } = await supabase
      .from('planets')
      .select('id, name')
      .order('id', { ascending: true });
    
    if (planetErr) throw planetErr;
    
    const planetIdByName = new Map<string, number>();
    planetMeta?.forEach((p: any) => {
      if (p?.name && typeof p?.id === 'number') {
        planetIdByName.set(p.name, p.id);
      }
    });

    // 2. Fetch houses metadata (notes, actionables, cautions, remedies)
    const { data: housesData, error: housesErr } = await supabase
      .from('houses')
      .select('*')
      .order('id', { ascending: true });
    
    if (housesErr) throw housesErr;

    const houseMap = new Map<number, any>();
    housesData?.forEach((h: any) => {
      if (typeof h?.id === 'number') houseMap.set(h.id, h);
    });

    // 3. Build list of planet-house pairs to fetch
    const planetHousePairs: Array<{ planet: PlanetName; house: number }> = [];
    ALL_PLANETS.forEach(planet => {
      const placement = placements[planet];
      if (placement) {
        planetHousePairs.push({ planet, house: placement.house });
      }
    });

    // 4. Fetch planet_in_house_predictions - BATCH QUERY to avoid N*2 individual queries
    const pihByKey = new Map<string, any>();
    
    if (planetHousePairs.length > 0) {
      const planetIds = planetHousePairs.map(p => planetIdByName.get(p.planet)).filter(Boolean) as number[];
      const houseIds = planetHousePairs.map(p => p.house);
      
      // Single batched query for all planet-house combinations
      const { data: allPIH } = await supabase
        .from('planet_in_house_predictions')
        .select('*')
        .in('planet_id', planetIds);
      
      if (allPIH) {
        // Build lookup map
        const pihMap = new Map<string, any>();
        allPIH.forEach((row: any) => {
          const key = `${row.planet_id}:${row.house_id || 'null'}`;
          pihMap.set(key, row);
        });
        
        // Match each planet-house pair
        planetHousePairs.forEach(({ planet, house }) => {
          const pid = planetIdByName.get(planet);
          if (!pid) return;
          
          // Try exact match first
          const exactKey = `${pid}:${house}`;
          if (pihMap.has(exactKey)) {
            pihByKey.set(`${planet}:${house}`, pihMap.get(exactKey));
          } else {
            // Fallback to generic (house_id = null)
            const genericKey = `${pid}:null`;
            pihByKey.set(`${planet}:${house}`, pihMap.get(genericKey) || null);
          }
        });
      }
    }

    // 5. Fetch lordship predictions
    type LordshipKey = { lordHouse: number; destHouse: number };
    const lordshipPairs: Array<{ planet: Exclude<PlanetName, 'Rahu' | 'Ketu'>; key: LordshipKey }> = [];
    
    PLANETS_7.forEach(planet => {
      const dest = placements[planet]?.house;
      if (!dest) return;
      
      const ownSigns = OWN_SIGNS[planet] || [];
      ownSigns.forEach(ownSign => {
        for (let h = 1; h <= 12; h++) {
          if (houseSign(ascendantSign, h) === ownSign) {
            lordshipPairs.push({ 
              planet, 
              key: { lordHouse: h, destHouse: dest } 
            });
          }
        }
      });
    });

    // Fetch lord-in-house cache ONCE before the loop
    const lihCache = await getCachedLordInHousePredictions();

    const lordshipResults = lordshipPairs.map(({ planet, key }) => {
      const row = lihCache.get(key.destHouse) || null;
      return { planet, key, row };
    });

    const lordshipByKey = new Map<string, any>();
    lordshipResults.forEach(({ planet, key, row }) => {
      lordshipByKey.set(`${key.lordHouse}:${key.destHouse}`, { planet, row });
    });

    // 6. Build interpretation objects for each house
    const interpretations: HouseInterpretation[] = [];

    for (let h = 1; h <= 12; h++) {
      const houseMeta = houseMap.get(h);
      const signOfHouse = houseSign(ascendantSign, h);
      
      // Sign-in-house note
      const signNoteColById: Record<number, string> = {
        1: 'note_sign_aries', 2: 'note_sign_taurus', 3: 'note_sign_gemini',
        4: 'note_sign_cancer', 5: 'note_sign_leo', 6: 'note_sign_virgo',
        7: 'note_sign_libra', 8: 'note_sign_scorpio', 9: 'note_sign_sagittarius',
        10: 'note_sign_capricorn', 11: 'note_sign_aquarius', 12: 'note_sign_pisces'
      };
      const signNote = houseMeta?.[signNoteColById[signOfHouse]] ?? undefined;

      // Planets in this house
      const planetsInHouse: HouseInterpretation['planets'] = [];
      
      ALL_PLANETS.forEach(planet => {
        const placement = placements[planet];
        if (placement?.house !== h) return;

        const pih = pihByKey.get(`${planet}:${h}`);
        const lon = longitudes[planet];
        
        // Get nakshatra note
        let nakshatraNote: string | undefined;
        let nakshatraLordNote: string | undefined;
        
        if (pih && typeof lon === 'number') {
          const nakIndex = Math.floor((((lon % 360) + 360) % 360) / (360 / 27));
          const nakNames = ['ashwini','bharani','krittika','rohini','mrigashira','ardra','punarvasu','pushya','ashlesha','magha','purva_phalguni','uttara_phalguni','hasta','chitra','swati','vishakha','anuradha','jyeshtha','mula','purva_ashadha','uttara_ashadha','shravana','dhanishta','shatabhisha','purva_bhadrapada','uttara_bhadrapada','revati'];
          const nakCol = `nak_${nakNames[nakIndex]}`;
          
          if (pih[nakCol]) {
            nakshatraNote = pih[nakCol] as string;
          }
          
          // Nakshatra lord note (same column but from different row potentially)
          const nakLordCol = `nak_lord_${nakNames[nakIndex]}`;
          if (pih[nakLordCol]) {
            nakshatraLordNote = pih[nakLordCol] as string;
          }
        }

        // House-specific planet note
        const noteColByPlanet: Record<PlanetName, string> = {
          Sun: 'note_planet_sun', Moon: 'note_planet_moon', Mars: 'note_planet_mars',
          Mercury: 'note_planet_mercury', Jupiter: 'note_planet_jupiter', Venus: 'note_planet_venus',
          Saturn: 'note_planet_saturn', Rahu: 'note_planet_rahu', Ketu: 'note_planet_ketu'
        };
        const housePlanetNote = houseMeta?.[noteColByPlanet[planet]] ?? undefined;

        planetsInHouse.push({
          planet,
          interpretation: pih?.overview ?? undefined,
          nakshatraNote,
          housePlanetNote
        });
        
      });

      // Lordship information for this house
      const lordshipInfo: HouseInterpretation['lordship'] = [];
      
      PLANETS_7.forEach(planet => {
        const dest = placements[planet]?.house;
        if (!dest) return;
        
        const ownSigns = OWN_SIGNS[planet] || [];
        ownSigns.forEach(ownSign => {
          for (let lordHouse = 1; lordHouse <= 12; lordHouse++) {
            if (houseSign(ascendantSign, lordHouse) === ownSign && lordHouse === h) {
              // This house's lord is placed somewhere
              const lihData = lordshipByKey.get(`${lordHouse}:${dest}`);
              if (lihData?.row) {
                const row = lihData.row;
                
                lordshipInfo.push({
                  lord: planet,
                  position: `H${lordHouse} lord in H${dest}`,
                  interpretation: row[`lord${lordHouse}_pred`] ?? undefined,
                  overview: row[`lord${lordHouse}_pred`] ?? undefined,
                  strengths: row.strengths ?? undefined,
                  challenges: row.challenges ?? undefined,
                  remedies: row.remedies ?? undefined
                });
              }
            }
          }
        });
      });

      interpretations.push({
        house: h,
        overview: houseMeta?.description ?? undefined,
        planets: planetsInHouse.length > 0 ? planetsInHouse : undefined,
        lordship: lordshipInfo.length > 0 ? lordshipInfo : undefined,
        signNote
      });
    }

    return interpretations;

  } catch (err) {
    console.error('Error fetching house interpretations:', err);
    return [];
  }
}

// ===== END FILE: src/utils/fetchHouseInterpretations.ts =====
